import type { MaybeRef } from 'vue';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { useBreadcrumbJsonLd } from './useBreadcrumbJsonLd';
import {
  absolutePageUrlFromPath,
  resolveSeoTitle,
  useJsonLdShared,
  type BreadcrumbCrumb,
} from './useJsonLdShared';
import { useSiteManifestStore } from '~/app/stores/siteManifest';

interface PostBreadcrumbContent {
  _type?: string;
  seoSocial?: { metaTitle?: string };
  title?: string;
  slug?: string;
}

/**
 * Blog / press post trail: Home > Blog|Press > [Post Title].
 */
export function usePostBreadcrumbJsonLd(content: MaybeRef<PostBreadcrumbContent | null | undefined>) {
  const { t } = useI18n();
  const route = useRoute();
  const { localePath, baseUrl } = useJsonLdShared();
  const siteManifestStore = useSiteManifestStore();

  const pageUrl = computed(() => {
    if (!unref(content)) {
      return null;
    }
    return absolutePageUrlFromPath(baseUrl.value, route.path);
  });

  const crumbs = computed((): BreadcrumbCrumb[] | null => {
    const post = unref(content);
    const url = pageUrl.value;
    if (!post || !url) {
      return null;
    }

    const isPress = post._type === DOCUMENT.PRESS_RELEASES;
    const sectionSlug = isPress
      ? (siteManifestStore.slugs?.pressReleases || 'press')
      : (siteManifestStore.slugs?.blog || 'blog');
    const sectionName = isPress ? 'Press' : 'Blog';
    const title = resolveSeoTitle(post);
    if (!title) {
      return null;
    }

    const homepageUrl = `${baseUrl.value}${localePath('/')}`;
    return [
      { name: t('home'), item: homepageUrl },
      { name: sectionName, item: `${baseUrl.value}${localePath(`/${sectionSlug}`)}` },
      { name: title, item: url },
    ];
  });

  useBreadcrumbJsonLd(crumbs, pageUrl, 'post-breadcrumb-jsonld');
}
